const THEME_STYLE_ELEMENT_ID = "dynamic-theme-css";

const ENV_THEME_BASE_URL = `${process.env.REACT_APP_CORE_API_URL}/mtekcss`;

export function getTenantHost(): string | null {
    if (typeof window !== "undefined" && window.location?.hostname) {
        // TESTING BLOCK
        // if (window.location.hostname == "localhost") {
        //     return "standardinsurance.hillcroftinsurance.com"
        // }
        return window.location.hostname;
    }
    return null;
}

function buildThemeUrl(): string | null {

    if (!ENV_THEME_BASE_URL) {
        return null;
    }

    const tenantHost = getTenantHost();
    if (!tenantHost) {
        return null;
    }

    try {
        const url = new URL(ENV_THEME_BASE_URL);
        url.searchParams.set("tenantHost", tenantHost);
        return url.toString();
    } catch {
        const base = ENV_THEME_BASE_URL.replace(/\/+$/, "");
        return `${base}?tenantHost=${encodeURIComponent(tenantHost)}`;
    }
}

function removeExistingThemeStyle() {
    const existing = document.getElementById(THEME_STYLE_ELEMENT_ID);
    if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
    }
}

function buildMappingBlock(css: string): string {
    const get = (varName: string): string => {
        const match = css.match(new RegExp(`${varName}\\s*:\\s*([^;]+);`));
        return match ? match[1].trim() : "";
    };

    const primaryColor = get("--color-primary") || "#00bac7";
    const secondaryColor = get("--color-secondary") || "#170f4f";
    const warningBg = get("--color-warning") || "#fef0c7";
    const warningFg = get("--color-on-warning") || "#170f4f";
    const dangerBg = get("--color-danger") || "#fef0c7";
    const dangerFg = get("--color-on-danger") || "#170f4f";

    return `
:root {
  /* App variable mappings from API theme */
  --primary-color: ${primaryColor};
  --secondary-color: ${secondaryColor};
  --page-background-color: #ffffff;
  --quote-background-color: #ffffff;

  --errorBgColor: ${dangerBg};
  --errorFgColor: ${dangerFg};
  --errorBorderColor: ${dangerBg};

  --warningBgColor: ${warningBg};
  --warningFgColor: ${warningFg};
  --warningBorderColor: ${warningBg};

  --toastify-color-warning: var(--warningBgColor);
  --toastify-color-error: var(--errorBgColor);
  --toastify-text-color-warning: var(--warningFgColor);
  --toastify-text-color-error: var(--errorFgColor);
  --toastify-icon-color-warning: var(--warningFgColor);
  --toastify-icon-color-error: var(--errorFgColor);
}`;
}

function applyThemeCss(css: string) {
    removeExistingThemeStyle();
    const style = document.createElement("style");
    style.id = THEME_STYLE_ELEMENT_ID;
    style.type = "text/css";
    // Append the API CSS as-is, then add the mapping block after so it wins the cascade
    style.textContent = css + buildMappingBlock(css);
    document.head.appendChild(style);
}

async function fetchThemeCss(url: string): Promise<string> {
    const response = await fetch(url, {
        method: "GET",
        headers: {
            Accept: "text/css",
        },
        mode: "cors",
    });

    if (!response.ok) {
        throw new Error(`Theme fetch failed with status ${response.status}`);
    }

    return await response.text();
}

export async function loadThemeCss(): Promise<void> {
    if (typeof document === "undefined") {
        return;
    }

    const themeUrl = buildThemeUrl();
    if (!themeUrl) {
        console.warn(
            "Dynamic theme URL is not configured. Set REACT_APP_THEME_URL or REACT_APP_THEME_BASE_URL. " +
            "Tenant host is read from window.location.hostname.",
        );
        return;
    }

    try {
        const themeCss = await fetchThemeCss(themeUrl);
        applyThemeCss(themeCss);
    } catch (error) {
        console.warn("Unable to load dynamic theme CSS:", error);
    }
}